import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { JOBS_PUBLIC_ENDPOINT } from 'src/constants';
import { getAuthenticatedUser, getTokenFromAuthenticatedUser } from 'src/features/login';
import { captureSentryError } from 'src/features/login/utils/captureSentryError';
import { getMandatoryEnvVar } from 'src/utils/env';
import {
    isJobAgendaErrorResponse,
    jobAgendaResponseSchema,
    JobAgendaSuccessResponse,
} from './getJobAgenda.types';

export const getJobAgenda = async (
    from: string,
    to?: string,
    status?: string,
    limit?: number,
    cursor?: number
): Promise<JobAgendaSuccessResponse> => {
    const user = await getAuthenticatedUser();
    const token = getTokenFromAuthenticatedUser(user);
    // getMandatoryEnvVar must live at this level in order for tests to successfully overwrite the process.env variable
    const API_BASE_URL = getMandatoryEnvVar('REACT_APP_API_BASE_URL');

    const requestOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
    };

    const paramsObj: Record<string, string> = {
        dateFrom: from,
    };
    if (to) paramsObj.dateTo = to;
    if (status) paramsObj.status = status;
    if (typeof limit !== 'undefined') paramsObj.limit = limit.toString();
    if (typeof cursor !== 'undefined') paramsObj.cursor = cursor.toString();

    const searchParams = new URLSearchParams(paramsObj);

    try {
        const response = await fetch(
            `${API_BASE_URL}/${JOBS_PUBLIC_ENDPOINT}/v1/engineer-jobs/agenda?${searchParams}`,
            requestOptions
        );
        const data = await response.json();

        const ajv = new Ajv({ coerceTypes: true });
        addFormats(ajv); // add formats for date-time format
        const validate = ajv.compile(jobAgendaResponseSchema);

        const valid = validate(data);
        if (valid) {
            if (isJobAgendaErrorResponse(data)) {
                throw new Error(`Error message from api: ${data.message}`);
            }
            return data;
        }
        throw new Error(
            `Failed to validate all jobs response. Error in ${JSON.stringify(
                validate.errors?.[0].schemaPath
            )} - ${JSON.stringify(validate.errors?.[0].params)}`
        );
    } catch (error) {
        await captureSentryError(error, { feature: 'getJobAgenda' });
        throw new Error(`Failed to get job agenda: ${error.message}`);
    }
};
